import React from 'react'
// libs
import { change, formValueSelector } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
// hocs
import withAddForm from './withAddForm'
// components
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { ClickAwayListener, Grid2 as Grid } from '@mui/material'
// views
import { AddButton, ButtonsRow, CloseIcon, CreateButton, Form, FormContent, InputWrapper } from './views'
// constants
import { ENTITIES } from 'constants/entities'
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// context
import { ObjectivesListAPIContext } from 'containers/ObjectivesList/layers/API'
import { ObjectivesListTreeBLContext } from 'containers/ObjectivesList/layers/BL/Tree'
// types
import { IPlanTypeLevelOption } from 'types/graphql'
// utils
import isEmpty from 'lodash/isEmpty'
import upperFirst from 'lodash/upperFirst'

const getButtons = (
  planAddObjective: boolean,
  planAddAction: boolean,
  planAddKPI: boolean,
  level: Record<string, any>
) => {
  return [
    {
      label: level.description || OBJECT_ENTITY_NAMES.GROUP,
      value: ENTITIES.PLAN_OBJECTIVE,
      active: planAddObjective
    },
    {
      label: OBJECT_ENTITY_NAMES.ACTION,
      value: ENTITIES.PLAN_ACTION,
      active: planAddAction
    },
    {
      label: OBJECT_ENTITY_NAMES.KPI,
      value: ENTITIES.PLAN_KPI,
      active: planAddKPI
    }
  ].filter((button) => button.active)
}

interface Props {
  form: any
  level: any
  submit: any
  rootType: any
  buttonProps: any
  handleClose: any
  handleSubmit: any
}

const AddRootEntityForm: React.FC<Props> = ({
  form,
  level,
  submit,
  rootType = {},
  buttonProps,
  handleClose,
  handleSubmit
}) => {
  const dispatch = useDispatch()
  const selector = formValueSelector(form)

  const { plan } = React.useContext(ObjectivesListTreeBLContext)

  const name = useSelector((state) => selector(state, 'name'))

  const { planAddAction, planAddKPI, planAddObjective } = plan.currentPermissions

  const { createRootEntityFormStateRef, createRootEntityRootTypeRef } = React.useContext(ObjectivesListAPIContext)

  const [isForm, setIsForm] = React.useState(createRootEntityFormStateRef.current)
  const [rootEntityType, setRootEntityType] = React.useState(createRootEntityRootTypeRef.current)

  const inputRef = React.useRef(null)

  const levelOptions: IPlanTypeLevelOption[] = level.typeOptions || []

  React.useEffect(() => {
    if (rootEntityType.value) {
      dispatch(change(form, 'rootType', rootEntityType))
    }
  }, [rootEntityType])

  // methods
  const openCreateForm = (entity) => {
    const rootType = { typeOption: null, ...entity }

    setIsForm(true)
    createRootEntityFormStateRef.current = true
    setRootEntityType(rootType)
    createRootEntityRootTypeRef.current = rootType

    if (inputRef.current && inputRef.current.focus) {
      inputRef.current.focus()
    }
  }

  const handleLevelOptionClick = (entity, levelOption: IPlanTypeLevelOption) => () =>
    openCreateForm({ ...entity, label: levelOption.label, typeOption: levelOption })

  const handleCreateClick = (entity) => () => openCreateForm(entity)

  const handleIconClose = () => {
    setIsForm(false)
    createRootEntityFormStateRef.current = false
    handleClose()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose()
      setIsForm(false)
      createRootEntityFormStateRef.current = false
    }
  }

  const handleClickAway = () => {
    if (isEmpty(rootEntityType)) {
      return
    } else if (name) {
      submit()
    }

    setIsForm(false)
    createRootEntityFormStateRef.current = false
  }

  const buttons = getButtons(planAddObjective, planAddAction, planAddKPI, level)

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <FormContent container spacing={2} direction='column'>
          {isForm && (
            <Grid container alignItems='center'>
              <InputWrapper>
                <ReduxInput
                  autoFocus
                  inputRef={inputRef}
                  name={'name'}
                  placeholder={`Enter a description for this ${rootEntityType.label || rootType.label}`}
                />
              </InputWrapper>
              <Grid>
                <AddButton {...buttonProps} type={'submit'}>
                  Add
                </AddButton>
              </Grid>
              <Grid>
                <CloseIcon onClick={handleIconClose} />
              </Grid>
            </Grid>
          )}

          <ButtonsRow container direction={'row'} wrap={'nowrap'} spacing={1}>
            {buttons.map((button) => {
              const hasLevelOptions = button.value === ENTITIES.PLAN_OBJECTIVE && !!levelOptions.length

              return (
                <React.Fragment key={button.value}>
                  <CreateButton {...buttonProps} type={'button'} onClick={handleCreateClick(button)}>
                    + Create {upperFirst(button.label)}
                  </CreateButton>

                  {hasLevelOptions &&
                    levelOptions.map((levelOption) => (
                      <CreateButton
                        key={levelOption.id}
                        {...buttonProps}
                        type={'button'}
                        onClick={handleLevelOptionClick(button, levelOption)}>
                        + Create {upperFirst(levelOption.label)}
                      </CreateButton>
                    ))}
                </React.Fragment>
              )
            })}
          </ButtonsRow>
        </FormContent>
      </Form>
    </ClickAwayListener>
  )
}

export default withAddForm(AddRootEntityForm)
